import React, { useState } from 'react';
import { Typography } from 'antd';
import { EnvironmentOutlined, RightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { LogoOrInitials } from './LogoOrInitials';
import { StatusBadge } from './StatusBadge';
import { WeeklySchedulePanel } from '../business-info/WeeklySchedulePanel';
import { useHomeStrings } from '../../hooks/useHomeStrings';
import type { HomeBusiness } from '../../model/types';

interface Props {
  business: HomeBusiness;
}

export const BusinessHero: React.FC<Props> = ({ business }) => {
  const STRINGS = useHomeStrings();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const hasSchedule = (business.weeklySchedule ?? []).length > 0;

  return (
    <div
      style={{
        background: 'var(--bl-primary)',
        marginBottom: 12,
        padding: '20px 16px 16px',
      }}
    >
      {/* Logo + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            flexShrink: 0,
            borderRadius: 16,
            border: '3px solid rgba(255,255,255,0.3)',
            overflow: 'hidden',
            lineHeight: 0,
          }}
        >
          <LogoOrInitials name={business.name} logoUrl={business.logoUrl} size={72} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
            <Typography.Text
              style={{
                display: 'block',
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--bl-primary-text)',
                letterSpacing: -0.5,
                lineHeight: 1.25,
                wordBreak: 'break-word',
                flex: 1,
                minWidth: 0,
              }}
            >
              {business.name}
            </Typography.Text>
            <StatusBadge status={business.status} />
          </div>
          {business.subtitle && (
            <Typography.Text
              style={{
                display: 'block',
                fontSize: 13,
                color: 'var(--bl-primary-text)',
                opacity: 0.72,
                lineHeight: 1.4,
                marginTop: 2,
              }}
            >
              {business.subtitle}
            </Typography.Text>
          )}
        </div>
      </div>

      {/* Info rows */}
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {business.address && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <EnvironmentOutlined
              style={{
                fontSize: 13,
                color: 'var(--bl-primary-text)',
                opacity: 0.65,
                flexShrink: 0,
              }}
            />
            <Typography.Text
              style={{
                fontSize: 13,
                color: 'var(--bl-primary-text)',
                opacity: 0.75,
              }}
            >
              {business.address}
            </Typography.Text>
          </div>
        )}
        {business.todayWorkingHours && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ClockCircleOutlined
              style={{
                fontSize: 13,
                color: 'var(--bl-primary-text)',
                opacity: 0.65,
                flexShrink: 0,
              }}
            />
            <Typography.Text
              style={{
                fontSize: 13,
                color: 'var(--bl-primary-text)',
                opacity: 0.75,
              }}
            >
              {STRINGS.HERO_TODAY_PREFIX}{business.todayWorkingHours}
            </Typography.Text>
          </div>
        )}
      </div>

      {/* Schedule link */}
      {hasSchedule && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setScheduleOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setScheduleOpen(true);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 10,
            padding: '12px 0',
            minHeight: 44,
            cursor: 'pointer',
            borderRadius: 6,
          }}
        >
          <Typography.Text
            style={{
              fontSize: 13,
              color: 'var(--bl-primary-text)',
              opacity: 0.90,
              fontWeight: 500,
            }}
          >
            {STRINGS.SCHEDULE_VIEW}
          </Typography.Text>
          <RightOutlined
            style={{
              fontSize: 10,
              color: 'var(--bl-primary-text)',
              opacity: 0.90,
            }}
          />
        </div>
      )}

      <WeeklySchedulePanel
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        schedule={business.weeklySchedule}
      />
    </div>
  );
};
